// ================================================================
// PROPRIETARY & CONFIDENTIAL — Mt Forge Inc.
// Copyright (c) 2026 Mt Forge. All rights reserved.
// Unauthorized copying, reverse engineering, or AI-assisted
// reproduction of this software is strictly prohibited.
// Protected under US Copyright Law (17 U.S.C.) and international
// treaties. Patent Pending. License: PROPRIETARY
// ================================================================
import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./AuthContext";
import Layout from "./components/Layout";
import LoginPage from "./pages/LoginPage";
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import PasswordChangePage from "./pages/PasswordChangePage";
import NDASignaturePage from "./pages/NDASignaturePage";
import AppTutorialPage from "./pages/AppTutorialPage";
import OverwatchPage from "./pages/OverwatchPage";
import DashboardPage from "./pages/DashboardPage";
import TaskBoardPage from "./pages/TaskBoardPage";
import MyTasksPage from "./pages/MyTasksPage";
import TeamTasksPage from "./pages/TeamTasksPage";
import SprintsPage from "./pages/SprintsPage";
import TeamDirectoryPage from "./pages/TeamDirectoryPage";
import RolesPermissionsPage from "./pages/RolesPermissionsPage";
import PerformancePage from "./pages/PerformancePage";
import ContactsPage from "./pages/ContactsPage";
import DealsPipelinePage from "./pages/DealsPipelinePage";
import ClientMeetingsPage from "./pages/ClientMeetingsPage";
import FollowUpsPage from "./pages/FollowUpsPage";
import ActivityFeedPage from "./pages/ActivityFeedPage";
import AnnouncementsPage from "./pages/AnnouncementsPage";
import MeetingNotesPage from "./pages/MeetingNotesPage";
import VisitLogPage from "./pages/VisitLogPage";
import VisitReportsPage from "./pages/VisitReportsPage";
import CallLogPage from "./pages/CallLogPage";
import CallRemindersPage from "./pages/CallRemindersPage";
import CallScriptsPage from "./pages/CallScriptsPage";
import ActiveProjectsPage from "./pages/ActiveProjectsPage";
import ProjectArchivePage from "./pages/ProjectArchivePage";
import MilestonesPage from "./pages/MilestonesPage";
import WeeklyReportPage from "./pages/WeeklyReportPage";
import KPIDashboardPage from "./pages/KPIDashboardPage";
import TimeTrackingPage from "./pages/TimeTrackingPage";
import ProfilePage from "./pages/ProfilePage";
import MeetingBriefPage from "./pages/MeetingBriefPage";
import AppHubPage from "./pages/AppHubPage";
import NDATrackerPage from "./pages/NDATrackerPage";
import SignLogPage from "./pages/SignLogPage";
import LicenseManagerPage from "./pages/LicenseManagerPage";
import PeoplePage from "./pages/PeoplePage";
import ValuationPage from "./pages/ValuationPage";
import PlatformAnalyticsPage from "./pages/PlatformAnalyticsPage";
import DevPage from "./pages/DevPage";
import SharedFoldersPage from "./pages/SharedFoldersPage";
import TeamChatPage from "./pages/TeamChatPage";
import LegalWatermark from "./components/LegalWatermark";
import "./App.css";

function OnboardingGate({ children }) {
  const { user, onboardingStep, completeOnboardingStep } = useAuth();
  if (!user) return children;
  if (onboardingStep === 'password') return <PasswordChangePage onComplete={() => completeOnboardingStep('password')} />;
  if (onboardingStep === 'nda') return <NDASignaturePage onComplete={() => completeOnboardingStep('nda')} />;
  if (onboardingStep === 'tutorial') return <AppTutorialPage onComplete={() => completeOnboardingStep('tutorial')} />;
  return children;
}

// ── ProtectedRoute: auth check + onboarding gate ──
// Layout now persists via nested <Outlet> — no reinstantiation on nav
function ProtectedRoute() {
  const { user, loading } = useAuth();
  if (loading) return <div className="loading-screen"><div className="loading-spinner" /><p>Loading...</p></div>;
  if (!user) return <Navigate to="/login" />;
  return <OnboardingGate><Layout /></OnboardingGate>;
}

function ForgotPasswordGuard() {
  const { user } = useAuth();
  return user ? <Navigate to="/" /> : <ForgotPasswordPage />;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordGuard />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      {/* All protected routes nested under Layout — Layout mounts ONCE */}
      <Route path="/" element={<ProtectedRoute />}>
        <Route index element={<Navigate to="/dashboard" />} />
        <Route path="overwatch" element={<OverwatchPage />} />
        <Route path="dashboard" element={<DashboardPage />} />
        <Route path="tasks" element={<TaskBoardPage />} />
        <Route path="my-tasks" element={<MyTasksPage />} />
        <Route path="team-tasks" element={<TeamTasksPage />} />
        <Route path="sprints" element={<SprintsPage />} />
        <Route path="team" element={<TeamDirectoryPage />} />
        <Route path="roles" element={<RolesPermissionsPage />} />
        <Route path="performance" element={<PerformancePage />} />
        <Route path="contacts" element={<ContactsPage />} />
        <Route path="deals" element={<DealsPipelinePage />} />
        <Route path="client-meetings" element={<ClientMeetingsPage />} />
        <Route path="follow-ups" element={<FollowUpsPage />} />
        <Route path="activity" element={<ActivityFeedPage />} />
        <Route path="announcements" element={<AnnouncementsPage />} />
        <Route path="meeting-notes" element={<MeetingNotesPage />} />
        <Route path="visit-log" element={<VisitLogPage />} />
        <Route path="visit-reports" element={<VisitReportsPage />} />
        <Route path="call-log" element={<CallLogPage />} />
        <Route path="call-reminders" element={<CallRemindersPage />} />
        <Route path="call-scripts" element={<CallScriptsPage />} />
        <Route path="projects" element={<ActiveProjectsPage />} />
        <Route path="archive" element={<ProjectArchivePage />} />
        <Route path="milestones" element={<MilestonesPage />} />
        <Route path="weekly-report" element={<WeeklyReportPage />} />
        <Route path="kpi" element={<KPIDashboardPage />} />
        <Route path="time-tracking" element={<TimeTrackingPage />} />
        <Route path="profile" element={<ProfilePage />} />
        <Route path="meeting-brief" element={<MeetingBriefPage />} />
        <Route path="app-hub" element={<AppHubPage />} />
        <Route path="nda-tracker" element={<NDATrackerPage />} />
        <Route path="sign-log" element={<SignLogPage />} />
        <Route path="licenses" element={<LicenseManagerPage />} />
        <Route path="people" element={<PeoplePage />} />
        <Route path="valuation" element={<ValuationPage />} />
        <Route path="platform-analytics" element={<PlatformAnalyticsPage />} />
        <Route path="dev" element={<DevPage />} />
        <Route path="shared-folders" element={<SharedFoldersPage />} />
        <Route path="team-chat" element={<TeamChatPage />} />
      </Route>
      <Route path="*" element={<Navigate to="/dashboard" />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <LegalWatermark appName="MT Forge" />
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  );
}